import { point } from "@turf/helpers";
import snakeCase from "lodash.snakecase";
import { MapboxCategoryMapping } from "../../Constants/MapboxCategoryMapping.js";
import { Category, Type } from "../../Models/GeocodeResponse.js";
import { CdnService } from "../Cdn/CdnService.js";
import { ValidationService } from "../ValidationService.js";

export class ATMapboxTransformationService {
  /** type {CdnService} */
  _cdnService;
  /** type {Logger} */
  _logger;

  _categoryMapping;
  _categoryPriority;
  _postcodeMapping;
  /** @type {boolean} */
  _postcodeMappingEnabled;

  /**
   * Construct the ATMapboxTransformationService. This service facilitates the transformation from Mapbox API data to
   * the AT geocoder response API.
   * @param {MapboxApiOptions} mapbox Mapbox API configuration options
   * @param {string} cdnBaseUrl CDN Base URL
   * @param {Logger} logger Logger
   */
  constructor(mapbox, cdnBaseUrl, logger) {
    this._postcodeMappingEnabled = mapbox.postcodeMappingEnabled;
    this._cdnService = new CdnService(cdnBaseUrl);
    this._logger = logger;
  }

  /**
   * Create the geocoding result object from the given Mapbox response FeatureCollection.
   * @param {Feature<Point, any>[]} mapboxFeatures Mapbox response FeatureCollection features (see https://docs.mapbox.com/api/search/search/#response-forward-geocoding and https://docs.mapbox.com/api/search/search/#response-reverse-geocoding)
   * @param {number} [limit] Maximum number of results to return
   * @returns {Promise<GeocodeResultFeatures>} Geocoding result
   */
  async transformFeatures(mapboxFeatures, limit) {
    const [{ categoryMapping, categoryPriority }, postcodeMapping] = await Promise.all([
      this._getCategoryMapping(),
      this._getPostcodeMapping(),
    ]);

    return mapboxFeatures
      .map((mapboxFeature) => {
        return this._transformFeature(mapboxFeature, categoryMapping, categoryPriority, postcodeMapping);
      })
      .filter((feature) => !!feature)
      .slice(0, limit);
  }

  /**
   * Transform the Mapbox feature into a geocoding result.
   * @param {Feature<Point, any>} feature Mapbox feature
   * @param [categoryMapping] Category mapping definition for mapping Mapbox categories to geocoding result categories.
   *                          If not provided OTHER will be used
   * @param [categoryPriority] Category priority definition for determining priority of geocoding result categories.
   *                           If not provided the first matching geocoding result category will be used
   * @param [postcodeMapping] Postcode mapping definition for mapping postcode to suburb name. If not provided, postcode
   *                          mapping will not be performed.
   * @returns {Feature<Point, GeocodeResultProperties>}
   * @private
   */
  _transformFeature(feature, categoryMapping, categoryPriority, postcodeMapping) {
    let type, category;
    let name = feature.properties.feature_name;
    if (feature.properties.place_type.includes("poi")) {
      type = Type.POI;
      category = this._transformCategory(feature.properties.poi_category, categoryMapping, categoryPriority);

      // Do not include BTF POIs from Mapbox as they will be included from GTFS
      if ([Category.BUS_STOP, Category.FERRY_TERMINAL, Category.TRAIN_STATION].includes(category)) {
        return null;
      }
    } else if (
      feature.properties.place_type.includes("address") ||
      feature.properties.place_type.includes("locality") ||
      feature.properties.place_type.includes("street")
    ) {
      type = Type.ADDRESS;
    }

    return point(feature.geometry.coordinates, {
      address: this._buildAddress(feature.properties, postcodeMapping),
      category,
      displayText: this._buildDisplayText(feature.properties, postcodeMapping),
      mapboxCategory: feature.properties?.poi_category,
      name,
      type,
    });
  }

  _buildAddress(featureProperties, postcodeMapping) {
    let street = undefined;

    if (featureProperties.place_type.includes("poi")) {
      street = ATMapboxTransformationService._extractTextSegment(
        featureProperties.context?.find((parentFeature) => parentFeature.layer.includes("address"))?.name
      );
    } else if (featureProperties.place_type.includes("address")) {
      street = ATMapboxTransformationService._extractTextSegment(featureProperties.feature_name);
    }

    return {
      postcode: featureProperties.context?.find((parentFeature) => parentFeature.layer.includes("postcode"))?.name,
      place: featureProperties.context?.find((parentFeature) => parentFeature.layer.includes("place"))?.name,
      locality: this._getLocality(featureProperties, postcodeMapping),
      street,
    };
  }

  _buildDisplayText(featureProperties, postcodeMapping) {
    let displayText = featureProperties.feature_name;
    const locality = this._getLocality(featureProperties, postcodeMapping);
    const place = featureProperties.context?.find((parentFeature) => parentFeature.layer.includes("place"));
    if (locality) {
      displayText += `, ${locality}`;
    } else if (place) {
      displayText += `, ${place.name}`;
    }
    return displayText;
  }

  /**
   * Return one category from an array of categories from mapbox
   * @param {string[]} mapboxCategories Array of category from Mapbox
   * @param [categoryMapping]
   * @param [categoryPriority]
   * @returns {Category[keyof Category]} The definitive POI category
   */
  _transformCategory(mapboxCategories, categoryMapping, categoryPriority) {
    if (mapboxCategories?.length) {
      // Finding all the categories associated with the mapbox categories
      const categories = mapboxCategories.reduce((result, mapBoxCategory) => {
        const fixedMapBoxCategory = mapBoxCategory.toLowerCase();
        if (!categoryMapping?.has(fixedMapBoxCategory)) {
          return result;
        }

        const category = categoryMapping.get(fixedMapBoxCategory);
        return result.includes(category) ? result : result.concat(category);
      }, []);

      // If a list of priority has been set, then find the most important category to return
      if (categoryPriority) {
        const priorityCategory = categoryPriority?.find((category) => categories.includes(category));
        if (priorityCategory) {
          return priorityCategory;
        }
      }

      // Return the first category if no priority could be established
      if (categories.length) {
        return categories[0];
      }
    }

    this._logger?.info?.(`No category found for ${JSON.stringify(mapboxCategories)}`);
    return Category.OTHER;
  }

  _getLocality(featureProperties, postcodeMapping) {
    let locality = featureProperties.context?.find(({ layer }) => layer.includes("locality"))?.name;
    if (!locality && postcodeMapping) {
      const postcode = featureProperties.context?.find(({ layer }) => layer.includes("postcode"))?.name;
      if (!!postcode && postcode in postcodeMapping) {
        locality = postcodeMapping[postcode];
      }
    }
    return locality;
  }

  async _getCategoryMapping() {
    if (!this._categoryMapping || !this._categoryPriority) {
      try {
        const cdnData = await this._fetchCategoryMapping();
        ValidationService.validateCategoryMapping(cdnData);

        this._categoryPriority = cdnData?.priority?.map(ATMapboxTransformationService._fixCategoryName) ?? null;
        this._categoryMapping = this._buildCategoryMapping(cdnData);
      } catch (error) {
        this._logger?.error?.(`Failed to setup the category mapping: ${error.name} - ${error.message}`);
      }
    }
    return { categoryMapping: this._categoryMapping, categoryPriority: this._categoryPriority };
  }

  async _getPostcodeMapping() {
    if (this._postcodeMappingEnabled) {
      if (!this._postcodeMapping) {
        try {
          const cdnData = await this._fetchPostcodeMapping();
          ValidationService.validatePostcodeMapping(cdnData);

          this._postcodeMapping = cdnData;
        } catch (error) {
          this._logger?.error?.(`Failed to setup the postcode mapping: ${error.name} - ${error.message}`);
        }
      }
      return this._postcodeMapping;
    }
    return null;
  }

  async _fetchCategoryMapping() {
    try {
      return await this._cdnService.getBlob("/geocoder/category_mapping.json");
    } catch (error) {
      this._logger?.error?.(`Impossible to fetch the category mapping: ${error.name} - ${error.message}`);
      return null;
    }
  }

  async _fetchPostcodeMapping() {
    try {
      return await this._cdnService.getBlob("/geocoder/postcode_mapping.json");
    } catch (error) {
      this._logger?.error?.(`Impossible to fetch the postcode mapping: ${error.name} - ${error.message}`);
      return null;
    }
  }

  _buildCategoryMapping(cdnData) {
    const categoryMapping = new Map();
    ATMapboxTransformationService._addMappingToMap(categoryMapping, cdnData?.mapping ?? {});
    ATMapboxTransformationService._addMappingToMap(categoryMapping, MapboxCategoryMapping.map);
    return categoryMapping;
  }

  static _extractTextSegment(address) {
    if (!address) return undefined;
    return address.split(",")[0].trim() || undefined;
  }

  static _fixCategoryName(name) {
    return snakeCase(name).toUpperCase();
  }

  /**
   * Enrich the map with the provided mapping
   * @param {Map} map
   * @param {Object} mapping
   */
  static _addMappingToMap(map, mapping) {
    for (const [mappingCategoryKey, mappingCategoryValues] of Object.entries(mapping)) {
      const fixedMappingCategoryKey = ATMapboxTransformationService._fixCategoryName(mappingCategoryKey);
      for (const mappingCategoryValue of mappingCategoryValues) {
        const fixedMappingCategoryValue = mappingCategoryValue.toLowerCase();
        if (!map.has(fixedMappingCategoryValue)) {
          map.set(fixedMappingCategoryValue, fixedMappingCategoryKey);
        }
      }
    }
  }
}
